import { AdminRole } from './core/auth/models/admin-user-info';
import { CountryFeature, isFeatureAvailable } from './core/country/country-features';
import { CountryCode } from './core/country/country.model';

/**
 * One sidebar navigation entry. Items with `children` render as an expansion panel;
 * plain items render as a single link. Adding a feature to the sidebar means adding
 * one entry (or one child) to `APP_NAV` — the template loop does the rest.
 */
export interface AppNavItem {
    label: string;
    /** Material icon name; shown for top-level items (children render as plain links). */
    icon?: string;
    /** Route for plain links; omit for group items with children. */
    route?: string;
    children?: AppNavItem[];
    /** When set, the item is visible only to users with exactly this role. */
    requiredRole?: AdminRole;
    /** When set, the item is visible only to users whose role is one of these. */
    requiredRoles?: AdminRole[];
    /** When set, the item is visible only while the active country supports the feature (G8). */
    countryFeature?: CountryFeature;
    /**
     * When set, the item is hidden on the production deployment.
     *
     * Cosmetic only, and deliberately so: the route's own `stagingOnlyGuard` and the service's
     * `Editor:Enabled=false` are what keep production from being edited. This only stops offering staff
     * a link that would immediately bounce.
     */
    stagingOnly?: boolean;
    /** `data-test-id` for E2E locators. */
    testId?: string;
}

export const APP_NAV: AppNavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    { label: 'Workers', icon: 'person_search', route: '/customers/workers', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-customers-workers' },
    { label: 'Employers', icon: 'work', route: '/customers/employers', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-customers-employers' },
    // The two catalog moderation queues sit next to the customer lists they belong to, not under Moderation:
    // as plain items they carry the role gate the Moderation group used to give them.
    { label: 'Resumes', icon: 'description', route: '/resume-moderation', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-resume-moderation' },
    { label: 'Vacancies', icon: 'assignment', route: '/vacancy-moderation', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-vacancy-moderation' },
    {
        label: 'Ordering',
        icon: 'shopping_cart',
        children: [
            { label: 'Subscriptions', route: '/subscriptions', testId: 'nav-subscriptions' },
            { label: 'Payments', route: '/payments', testId: 'nav-payments' },
            { label: 'Tariffs', route: '/tariffs', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-tariffs' },
            { label: 'Bonus Transactions', route: '/bonuses', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-bonuses' },
            { label: 'Disputes', route: '/disputes', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-disputes' },
            { label: 'Regular Promos', route: '/promos/regular', testId: 'nav-promos-regular' },
            { label: 'Personal Promos', route: '/promos/personal', testId: 'nav-promos-personal' },
            { label: 'Mass Promo Configs', route: '/promos/mass-configs', testId: 'nav-promos-mass-configs' },
            { label: 'Event Promo Configs', route: '/promos/event-configs', testId: 'nav-promos-event-configs' },
            { label: 'Referral System', route: '/referrals', requiredRoles: [AdminRole.Admin, AdminRole.Moderator], testId: 'nav-referrals' },
        ],
    },
    {
        label: 'Unmanaged Ordering',
        icon: 'sync_alt',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        countryFeature: 'unmanagedOrdering',
        children: [
            { label: 'Unmanaged Subscriptions', route: '/unmanaged-ordering/subscriptions', testId: 'nav-unmanaged-subscriptions' },
            { label: 'Unmanaged Payments', route: '/unmanaged-ordering/payments', testId: 'nav-unmanaged-payments' },
        ],
    },
    {
        label: 'Moderation',
        icon: 'verified_user',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Background Checks', route: '/background-checks', countryFeature: 'backgroundChecks', testId: 'nav-background-checks' },
            { label: 'Phone Verifications', route: '/phone-verifications', testId: 'nav-phone-verifications' },
            { label: 'Complaints', route: '/customer-complaints', testId: 'nav-customer-complaints' },
            { label: 'Documents', route: '/documents', testId: 'nav-documents' },
            { label: 'Photos', route: '/photo-moderation', testId: 'nav-photo-moderation' },
            { label: 'Audio messages', route: '/audio-message-moderation', testId: 'nav-audio-message-moderation' },
            { label: 'Blacklists', route: '/blacklists', testId: 'nav-blacklists' },
        ],
    },
    {
        label: 'Chat',
        icon: 'forum',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Conversations', route: '/chats/conversations', testId: 'nav-chats' },
            { label: 'Chat Messages', route: '/chats/messages', testId: 'nav-chat-messages' },
            { label: 'Unread Users', route: '/chats/unread-users', testId: 'nav-chat-unread-users' },
        ],
    },
    {
        label: 'Notifications',
        icon: 'notifications',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            {
                label: 'Notification Configs',
                route: '/notifications/configs',
                testId: 'nav-notification-configs',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
            {
                label: 'Mass Mailings',
                route: '/notifications/mailings',
                testId: 'nav-notification-mailings',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
            {
                label: 'Sent Notifications',
                route: '/notifications/sent',
                testId: 'nav-notification-sent',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
            },
            { label: 'Delivery Logs', route: '/delivery-logs', testId: 'nav-delivery-logs' },
        ],
    },
    {
        label: 'SEO',
        icon: 'article',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'SEO Catalogs', route: '/seo/catalogs', testId: 'nav-seo-catalogs' },
            { label: 'SEO Selectors', route: '/seo/selectors', testId: 'nav-seo-selectors' },
        ],
    },
    {
        label: 'Localization',
        icon: 'translate',
        // The parent must carry the UNION of its children's roles: visibleNavItems runs the same
        // predicate on the group and each child, and a group that fails hides everything under it.
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator],
        children: [
            {
                label: 'Translations',
                route: '/localization/tags',
                testId: 'nav-localization-tags',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator],
                stagingOnly: true,
            },
            {
                // Reachable in production on purpose: exporting from staging and importing here is how
                // translations reach a production region at all.
                label: 'Import & export',
                route: '/localization/transfer',
                testId: 'nav-localization-transfer',
                requiredRoles: [AdminRole.Admin, AdminRole.Moderator, AdminRole.Translator],
            },
        ],
    },
    {
        label: 'AI Support',
        icon: 'support_agent',
        children: [
            { label: 'Support Cards', route: '/support-knowledge/cards', testId: 'nav-support-cards' },
            { label: 'AI Conversations', route: '/ai-conversations', testId: 'nav-ai-conversations' },
        ],
    },
    {
        label: 'AI Search',
        icon: 'search',
        children: [
            { label: 'Requests', route: '/ai-search-requests' },
            { label: 'Analytics', route: '/ai-search-analytics' },
        ],
    },
    {
        label: 'Reports',
        icon: 'insights',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Employer statistics', route: '/reports/employer-stats', testId: 'nav-reports-employer-stats' },
            { label: 'Employer payments', route: '/reports/employer-payments', testId: 'nav-reports-employer-payments' },
            { label: 'Worker statistics', route: '/reports/worker-stats', testId: 'nav-reports-worker-stats' },
            { label: 'Worker payments', route: '/reports/worker-payments', testId: 'nav-reports-worker-payments' },
            { label: 'Registrations', route: '/reports/registrations', testId: 'nav-reports-registrations' },
            { label: 'Conversations statistics', route: '/reports/chat-stats', testId: 'nav-reports-chat-stats' },
            { label: 'Chat abuse', route: '/reports/chat-abuse', testId: 'nav-reports-chat-abuse' },
            { label: 'Contact limit exceeded', route: '/reports/contact-limit', testId: 'nav-reports-contact-limit' },
            { label: 'Sex offenders', route: '/reports/sex-offenders', testId: 'nav-reports-sex-offenders' },
        ],
    },
    {
        label: 'Geo',
        icon: 'travel_explore',
        requiredRoles: [AdminRole.Admin, AdminRole.Moderator],
        children: [
            { label: 'Geo Units', route: '/geo/units', testId: 'nav-geo-units' },
            { label: 'Geo Lookup', route: '/geo/lookup', testId: 'nav-geo-lookup' },
        ],
    },
    {
        label: 'Schedulers',
        icon: 'schedule',
        requiredRole: AdminRole.Admin,
        children: [
            { label: 'Jobs', route: '/scheduler/jobs', testId: 'nav-scheduler-jobs' },
            { label: 'Executing', route: '/scheduler/executing', testId: 'nav-scheduler-executing' },
            { label: 'Analytics', route: '/scheduler/analytics', testId: 'nav-scheduler-analytics' },
        ],
    },
    {
        label: 'Other',
        icon: 'monitoring',
        requiredRole: AdminRole.Admin,
        children: [
            { label: 'Users & Roles', route: '/users', testId: 'nav-users' },
            { label: 'Settings', route: '/settings', testId: 'nav-settings' },
        ],
    },
];

/**
 * Role-, country- and deployment-filters the nav model (pure — consumed by the app store's
 * `visibleNav` computed, which re-evaluates when the active country switches). Groups whose children
 * are all filtered out disappear entirely.
 *
 * `isProdDeployment` is required rather than defaulted: a default would decide, silently and in
 * favour of showing more, what every future caller that forgets it gets.
 */
export function visibleNavItems(
    role: AdminRole | undefined,
    country: CountryCode,
    isProdDeployment: boolean,
): AppNavItem[] {
    const isVisible = (item: AppNavItem): boolean => {
        if (item.requiredRole && item.requiredRole !== role) return false;
        if (item.requiredRoles && !(role !== undefined && item.requiredRoles.includes(role))) return false;
        if (item.countryFeature && !isFeatureAvailable(item.countryFeature, country)) return false;
        if (item.stagingOnly && isProdDeployment) return false;
        return true;
    };
    return APP_NAV
        .filter(isVisible)
        .map(item => item.children ? { ...item, children: item.children.filter(isVisible) } : item)
        .filter(item => !item.children || item.children.length > 0);
}
